import { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from 'lucide-react'
import { Button } from '@/components/ui/Button/Button'
import { cn } from '@/lib/utils/cn'

export interface RichTextEditorProps {
  /** Current HTML value. */
  value: string
  /** Fires with the editor's HTML on every change. */
  onChange: (html: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

/**
 * Minimal TipTap-backed WYSIWYG. Emits HTML via `onChange` and edits a
 * formatted body — bold / italic / lists / links. Styled entirely from design
 * tokens; the editing surface uses normal prose (never `font-mono`).
 */
export function RichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'outline-none min-h-32 text-role-data-secondary leading-normal',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
  })

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [editor, disabled])

  // `content` above only seeds the editor at mount. Sync later external
  // changes (a drafted body, an inserted paragraph) without echoing the
  // editor's own edits back into it, which would reset the cursor.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  const toggleLink = () => {
    if (!editor) return
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const toolBtn = (
    label: string,
    Icon: typeof Bold,
    active: boolean,
    onClick: () => void,
  ) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      iconOnly
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      data-active={active || undefined}
      className={cn(active && 'bg-surface-active text-primary')}
      onClick={onClick}
    >
      <Icon aria-hidden="true" size={16} />
    </Button>
  )

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-0.5 rounded-sm border border-border-subtle bg-surface-sunk px-1 py-1">
        {toolBtn('Bold', Bold, !!editor?.isActive('bold'), () =>
          editor?.chain().focus().toggleBold().run(),
        )}
        {toolBtn('Italic', Italic, !!editor?.isActive('italic'), () =>
          editor?.chain().focus().toggleItalic().run(),
        )}
        {toolBtn('Bullet list', List, !!editor?.isActive('bulletList'), () =>
          editor?.chain().focus().toggleBulletList().run(),
        )}
        {toolBtn(
          'Ordered list',
          ListOrdered,
          !!editor?.isActive('orderedList'),
          () => editor?.chain().focus().toggleOrderedList().run(),
        )}
        {toolBtn('Link', LinkIcon, !!editor?.isActive('link'), toggleLink)}
      </div>
      <EditorContent
        editor={editor}
        className="rounded-sm border border-border-default bg-surface p-2 [&_a]:text-accent [&_a]:underline"
      />
    </div>
  )
}
